/**
 *
 * Distributed under the MIT license. See LICENSE file for details.
 * Copyright (c) 2019 vhall
 */
import Component from './../../core/Component';
import { KV } from './../../core/Constant';
import { PlayerEvent } from './../../PlayerEvents';
import VhyReporter from '@vhallyun/log-reporter';
import log from 'loglevel';

const CALC_PLAY_TIME_INTERVAL = 200;
const BLOCK_MONITOR_INTERVAL_MS = 60 * 1000;
const WAITING_TO_PLAYING_MIN_BLOCK_MS = 200;

/**
 * 抽象日志发送类
 *
 * @export
 * @class AbstractReporter
 */
export default class AbstractReporter extends Component {
  constructor() {
    super();
    this.body = {};
    this.allConfig = null;
    this._config = null;
    this._player = null;
    
    //30s上报观看信息间隔
    this._infoPackIntervalMs = 30 * 1000;
    //30s上报观看信息定时器
    this._infoPackInterval = 0;
    // session检查时间戳（每超过24小时更换session）
    this._sessionCheckTimestamp = Date.now();
    // 心跳日志发送时间戳（更换session_id使用）
    this._infoPackTimestamp = Date.now();
    // session重置计数
    this._resetSessionCount = 0;

    // 计算观看时长定时器 - 上报观看时长 tt 用的
    this._playTimeInterval = 0;
    // 在一次上报周期内，真实的音视频播放时长
    this._playInfoDuration = 0;
    // 上一次计算播放时长的 - 相对时间
    this._lastCalcPlayTime = Date.now();

    /** 卡顿次数 */
    this._lagCount = 0;
    /** 卡顿时长 */
    this._bt = 0;

    /** video event  集合 */
    this._playState = 'begin ';

    /** 上一个信息包下载量 */
    this._lastInfoDownloadSize = 0;

    this._lastP2PDownloadSize = 0;

    //卡顿监测信息
    this.blockMonitorInterval = 0;
    this.blockTimeline = {
      waitingTime: 0,
      playingTime: 0,
      trunkSendTime: 0,
    }
    this.blockInfo = {
      blockCount: 0,
      blockDuration: 0,
    }
  }

  setReportConfig(reportConfig, otherOption) {
    // this.timeline = JSON.parse(JSON.stringify(this.player.timeline));
    this.timeline = Object.assign({}, this.player.timeline);
    this.timeline["t_autoplay"] = this.player._config.autoplay ? 0 : -1;
    this.timeline["t_srcswitch"] = 0;

    if (typeof reportConfig === 'string') {
      reportConfig = JSON.parse(reportConfig);
    }
    if (typeof otherOption === 'string') {
      otherOption = JSON.parse(otherOption);
    }

    this._config = reportConfig;
    this.body.pushTime = Date.now();
    this.body.protocol = this.allConfig.type || '';
    this.body.videoWidth = 0;
    this.body.videoHeight = 0;
    // 组合自定义上报字段
    Object.assign(this.body, otherOption);

    this._infoPackIntervalMs = parseInt(reportConfig.interval) * 1000;

    this.__ready();
  }

  set player(p) {
    this._player = p;
    this._handleCareEvent();
  }

  get player() {
    return this._player;
  }

  get httpDownloadSize() {
    let downloadSize = this.player.getDownloadSize();
    let tf = downloadSize - this._lastInfoDownloadSize;
    this._lastInfoDownloadSize = downloadSize;
    return tf;
  }

  get p2pDownloadSize() {
    if (window.peer5) {
      let stats = window.peer5.getStats();
      let downloadSize = stats ? stats.totalP2PDownloaded : 0;
      let tf = downloadSize - this._lastP2PDownloadSize;
      this._lastP2PDownloadSize = downloadSize;
      return tf;
    } else {
      return 0;
    }
  }

  /**
   * 当音频/视频处于加载过程中时，会依次发生以下事件：
   *  loadstart
      durationchange
      loadedmetadata
      loadeddata
      progress
      canplay
      canplaythrough
      play
      playing
   */
  _handleCareEvent() {
    this.player.on(PlayerEvent.LOADSTART, this.__loadstart.bind(this));
    this.player.on(PlayerEvent.LOADEDMETADATA, this.__loadedmetadata.bind(this));
    this.player.on(PlayerEvent.LOADED, this.__loaded.bind(this));
    this.player.on(PlayerEvent.CANPLAY, this.__canplay.bind(this));
    this.player.on(PlayerEvent.PLAY, this.__play.bind(this));
    this.player.on(PlayerEvent.PLAYING, this.__playing.bind(this));
    this.player.on(PlayerEvent.WAITING, this.__waiting.bind(this));
    this.player.on(PlayerEvent.PAUSE, this.__pause.bind(this));
    this.player.on(PlayerEvent.ENDED, this.__ended.bind(this));
    this.player.on(PlayerEvent.ERROR, this.__error.bind(this));
    this.player.on(PlayerEvent.AUTOPLAY_SUCCESS, this.__autoplaySuccess.bind(this));
    this.player.on(PlayerEvent.AUTOPLAY_FAILED, this.__autoplayFailed.bind(this));

    this.player.on('lagreport', this.__lag.bind(this));
    this.player.on('lagrecover', this.__lagRecover.bind(this));
    this.player.on(PlayerEvent.SRC_SWITCH, this.__srcSwitch.bind(this));
    this.player.on(PlayerEvent.SRC_CHANGED, this.__srcChange.bind(this));
    this.player.on(PlayerEvent.DEFINITION_CHANGED, this.__definitionchange.bind(this));
    this.player.on(PlayerEvent.TOUCH_PLAY, this.__touchPlay.bind(this));
    this.player.on(PlayerEvent.SCHEDULER_SUCCESS, this.__scheduler.bind(this));
    this.player.on(PlayerEvent.SCHEDULER_FAIL, this.__scheduler.bind(this));
    this.player.on(PlayerEvent.SCHEDULER_COMPLETE, this.__schedulercomplete.bind(this));
    this.player.on(PlayerEvent.CHANGEKERNEL, this.__changekernel.bind(this));
  }

  /**
   * 计算加载节点耗时
   * @returns 
   */
  __calcTimeOffset() {
    if(this.timeline["t_playertimestamp"]) {
      return Math.max(Date.now() - this.timeline["t_playertimestamp"], 0);
    }
    return 0
  }

  /**
   * 封装的数据上报方法
   * @param {*} code
   * @param {*} obj
   */
  __sendLog(code, obj = {}) {
    // 每超过24小时更换session
    // https://www.tapd.cn/tapd_fe/39181618/bug/detail/1139181618001025344
    if (Date.now() - this._sessionCheckTimestamp >= 24 * 60 * 60 * 1000) {
      this._sessionCheckTimestamp = Date.now();
      this._resetSessionCount += 1;
      this.body.s = this._config.session_id + '_' + this._resetSessionCount.toString();
      log.info(`超过24小时，更新上报session_id: ${this.body.s}`);
    }

    // 超过30分钟未上报心跳日志，更换session
    // 见http://wiki.vhallops.com/pages/viewpage.action?pageId=168559451
    // todo: 历史原因不明
    if (Date.now() - this._infoPackTimestamp >= 1800 * 1000) {
      this._infoPackTimestamp = Date.now();
      this._resetSessionCount += 1;
      this.body.s = this._config.session_id + '_' + this._resetSessionCount.toString();
      log.warn(`超过30分钟未发生上报，更新上报session_id: ${this.body.s}`);
    }

    // 播放地址
    let el = this.getURLInfo();
    this.body.sd = el.host;
    this.body.fd = el.pathname;
    if (el.protocol.indexOf('webrtc') > -1) {
      this.body.fd = el.pathname + '.rtl'; // 大数据计费添加rtl后缀
    }
    // 实际使用分辨率
    this.body.picture_quality = this._player?.currentDefinition?.def || this.body.picture_quality;
    Object.assign(obj, this.body);

    // 上报
    VhyReporter.scout(code, obj);
  }

  __ready() {
    //
  }

  /***
   * 调度成功或失败是触发
   */
  __scheduler(msg) {
    /*
    const k = this.allConfig.isLive === true ? 102011 : 92011;
    const obj = {
      errcode: msg.code,
      errmsg: msg.message,
      gslburl: this.player.schedulerUrl
    };
    this.__sendLog(k, obj);
    */
  }

  __schedulercomplete(msg) {
    if(!this.timeline["t_gslb"]) {
      this.timeline["t_gslb"] = this.__calcTimeOffset();
    }
  }

  /**
   * 开始加载音视频
   * @param {*} e 
   */
  __loadstart(e) {
    if(!this.timeline["t_loadstart"]) {
      this.timeline["t_loadstart"] = this.__calcTimeOffset();
    }
  }

  /**
   * 音频/视频的元数据包括：时长、尺寸（仅视频）以及文本轨道。
   * @param {*} e
   */
  __loadedmetadata(e) {
    if(!this.timeline["t_loadedmetadata"]) {
      this.timeline["t_loadedmetadata"] = this.__calcTimeOffset();
    }

    this._playState += 'la ';
    // 获取视频分辨率
    if (e.target) {
      this.body.videoWidth = e.target.videoWidth || 0;
      this.body.videoHeight = e.target.videoHeight || 0;
    }
  }

  /**
   * 已加载音视频数据
   * @param {*} e 
   */
  __loaded(e) {
    if(!this.timeline["t_loadeddata"]) {
      this.timeline["t_loadeddata"] = this.__calcTimeOffset();
    }
  }

  /**
   * 已准备好开始播放
   */
  __canplay() {
    if(!this.timeline["t_canplay"]) {
      this.timeline["t_canplay"] = this.__calcTimeOffset();
    }

    this._playState += 'canplay ';
  }

  /**
   * 自动播放成功
   * @param {*} e 
   */
  __autoplaySuccess(e) {
    if(!(this.timeline["t_autoplay"] > 0)) {
      this.timeline["t_autoplay"] = this.__calcTimeOffset();
    }
  }

  /**
   * 自动播放失败
   * @param {*} e 
   */
  __autoplayFailed(e) {
    if(!(this.timeline["t_autoplay"] > 0)) {
      this.timeline["t_autoplay"] = -2;
    }
  }

  /**
   * 已开始播放
   * @param {*} e 
   */
  __play(e) {
    if(!this.timeline["t_play"]) {
      this.timeline["t_play"] = this.__calcTimeOffset();
    }

    this._playState += 'play ';

    // 外层初始化时，已调用一次start()
    // 此外，如： 点播播放结束后，又点击进度条再次播放
    if (!this._infoPackInterval) {
      this.start();
    }
    // 开始播放状态后，才启动播放时长计算定时器
    if (!this._playTimeInterval) {
      this._lastCalcPlayTime = Date.now();
      this._playTimeInterval = setInterval(this._calcPlayTime.bind(this), CALC_PLAY_TIME_INTERVAL);
    }
  }

  /**
   * 已开始播放并渲染画面
   * @param {*} e 
   */
  __playing() {
    this._playState += 'playing ';

    // 只上报第一次playing，上报内容包含播放加载时间线
    if(!this.timeline["t_playing"]) {
      this.timeline["t_playing"] = this.__calcTimeOffset();
      this.timeline["t_totalplaying"] = this.timeline["t_createinstance"] + this.timeline["t_playing"];
      //此时可能还未收到autoplay的结果，因此等待100毫秒再上报
      setTimeout(() => {
        log.info("播放器加载timeline: ", this.timeline);
        const k = this.allConfig.isLive === true ? 102012 : 92012;
        const obj = Object.assign({}, this.timeline);
        delete obj.t_playertimestamp
        this.__sendLog(k, obj);
      }, 500);
    }

    // 开始播放状态后，才启动播放时长计算定时器
    // 触发顺序可能是play -> waiting -> playing，因此此处再次检查
    if (!this._playTimeInterval) {
      this._lastCalcPlayTime = Date.now();
      this._playTimeInterval = setInterval(this._calcPlayTime.bind(this), CALC_PLAY_TIME_INTERVAL);
    }

    //卡顿恢复，记录卡顿次数和卡顿时间
    const now = Date.now();
    if (this.blockTimeline.trunkSendTime > 0) {
      //卡顿状态被上报周期截断时
      if (now - this.blockTimeline.trunkSendTime > WAITING_TO_PLAYING_MIN_BLOCK_MS) {
        this.blockInfo.blockDuration += now - this.blockTimeline.trunkSendTime;
      }
    } else if (this.blockTimeline.waitingTime > 0) {
      //正在发生卡顿时
      if(now - this.blockTimeline.waitingTime > WAITING_TO_PLAYING_MIN_BLOCK_MS) {
        this.blockInfo.blockCount += 1;
        this.blockInfo.blockDuration += now - this.blockTimeline.waitingTime;
      }
    }
    this.blockTimeline.trunkSendTime = 0;
    this.blockTimeline.waitingTime = 0;
  }

  __definitionchange(e) {
    if (e.newValue && e.newValue.def) {
      this.body.picture_quality = e.newValue.def;
    }
  }

  __touchPlay() {
    //
  }

  __waiting() {
    this._playState += 'waiting ';
    // 停止记录播放时长
    if (this._playTimeInterval) {
      clearInterval(this._playTimeInterval);
      this._playTimeInterval = 0;
    }

    //卡顿监测，记录waiting时间点
    if (this.blockTimeline.waitingTime == 0) {
      this.blockTimeline.waitingTime = Date.now();
    }
  }

  __pause(e) {
    this._playState += 'pause ';
    // 停止记录播放时长
    if (this._playTimeInterval) {
      clearInterval(this._playTimeInterval);
      this._playTimeInterval = 0;
    }
  }

  __ended(e) {
    this._playState += 'end ';
  }

  __lag(e) {
    //
  }

  __lagRecover(t) {
    //
  }

  __error(e) {
    this._playState += 'error ';
    let k = this.allConfig.isLive === true ? 102017 : 92018;
    let obj = {
      playState: this._playState,
      videoUrl: this._player.store.info.url,
    };
    try {
      // e属性 存在JSON.stringify重复引用，
      obj.details = e.details;
      obj.type = e.type;
      obj.response = e.response || 'none';
      obj.info = e.info || 'none';
    } catch (e) {
      //
    }
    this.__sendLog(k, obj);
  }

  __srcSwitch(e) {
    this.timeline["t_srcswitch"] = this.__calcTimeOffset();
    // 停止记录播放时长
    if (this._playTimeInterval) {
      clearInterval(this._playTimeInterval);
      this._playTimeInterval = 0;
    }
  }

  __srcChange(e) {
    this._playState = 'begin ';
  }

  __changekernel(msg) {
    let k = 102018;
    let obj = {};
    obj.msgCode = msg.code;
    obj.msgDetail = msg.message;
    this.__sendLog(k, obj);
  }

  blockMonitor() {
    let k = this.allConfig.isLive === true ? 102030 : 92030;
    if (this.blockTimeline.waitingTime == 0) {
      //当前为正常状态
      const obj = {
        blkcount: this.blockInfo.blockCount,
        blkduration: this.blockInfo.blockDuration,
        blkperiod: BLOCK_MONITOR_INTERVAL_MS,
      }
      log.info('卡顿监测上报(常规)：', JSON.stringify(obj));
      this.__sendLog(k, obj);
      
      this.blockInfo.blockCount = 0;
      this.blockInfo.blockDuration = 0;
    } else {
      //当前为卡顿中状态，在上报周期内阶段，注意仅在第一个截断里增加卡顿次数
      const now = Date.now();
      const obj = {
        blkcount: this.blockTimeline.trunkSendTime == 0 ? this.blockInfo.blockCount + 1 : this.blockInfo.blockCount,
        blkduration: this.blockInfo.blockDuration + (now - this.blockTimeline.waitingTime),
        blkperiod: BLOCK_MONITOR_INTERVAL_MS,
      }
      log.info('卡顿监测上报(截断)：', JSON.stringify(obj));
      this.__sendLog(k, obj);
      
      this.blockInfo.blockCount = 0;
      this.blockInfo.blockDuration = 0;
      this.blockTimeline.waitingTime = now;
      this.blockTimeline.trunkSendTime = now;
    }
  }

  /**
   * 启动30s上报心跳定时器
   */
  start() {
    log.debug('启动日志上报');
    if (this._infoPackInterval) {
      clearInterval(this._infoPackInterval);
      this._infoPackInterval = 0;
    }
    this._infoPackInterval = setInterval(this.infoPack.bind(this), this._infoPackIntervalMs);

    if (this.blockMonitorInterval) {
      clearInterval(this.blockMonitorInterval);
      this.blockMonitorInterval = 0;
    }
    this.blockMonitorInterval = setInterval(this.blockMonitor.bind(this), BLOCK_MONITOR_INTERVAL_MS);
  }

  /**
   * 停止所有定时器
   */
  stop() {
    log.info('停止日志上报');
    this._playInfoDuration = 0;
    if (this._infoPackInterval) {
      clearInterval(this._infoPackInterval);
      this._infoPackInterval = 0;
    }
    if (this._playTimeInterval) {
      clearInterval(this._playTimeInterval);
      this._playTimeInterval = 0;
    }
    if (this.blockMonitorInterval) {
      clearInterval(this.blockMonitorInterval);
      this.blockMonitorInterval = 0;
    }
  }

  /**
   * 销毁
   *
   * @memberof AbstractReporter
   */
  destroy() {
    super.destroy();
    this.stop();

    this._lagCount = 0;
    this._bt = 0;
    this._playState = 'begin ';
    this._lastInfoDownloadSize = 0;
    this._lastCalcPlayTime = Date.now();
  }

  /**
   * 发送心跳信息包，具体查看继承实现
   *
   * @memberof AbstractReporter
   */
  infoPack() {
    this._lagCount = 0;
    this._bt = 0;
    this._playInfoDuration = 0;
    this._infoPackTimestamp = Date.now();
  }

  _calcPlayTime() {
    const now = Date.now();
    const elapsedTime = now - this._lastCalcPlayTime;
    this._playInfoDuration += elapsedTime;
    this._lastCalcPlayTime = now;
  }

  /**
   * 返回Location对象
   *
   * @memberof Reporter
   * @returns Location 对象
   */
  getURLInfo() {
    const { store } = this.player;
    let url = store.getKV(KV.URL);
    if (!this.el) {
      this.el = document.createElement('a');
    }
    let el = this.el;
    el.href = url;
    return el;
  }
}
